import { MoreHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/core/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';

interface OverflowMenuProps {
  onEdit?: () => void;
  onDelete?: () => void;
  'data-testid'?: string;
  /** CSS group name used for hover visibility. Default: 'row' → group-hover/row */
  groupName?: string;
  /** Extra classes forwarded to the trigger button (e.g. override opacity) */
  buttonClassName?: string;
}

export function OverflowMenu({
  onEdit,
  onDelete,
  'data-testid': testId,
  groupName = 'row',
  buttonClassName,
}: OverflowMenuProps) {
  const { t } = useTranslation('common');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                `size-6 opacity-0 group-hover/${groupName}:opacity-100 focus:opacity-100`,
                buttonClassName,
              )}
              data-testid={testId}
              aria-label={t('actions.more', 'More options')}
            >
              <MoreHorizontal className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('actions.more', 'More options')}</TooltipContent>
        </Tooltip>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {onEdit && (
          <DropdownMenuItem onClick={onEdit}>{t('actions.edit', 'Edit')}</DropdownMenuItem>
        )}
        {onDelete && (
          <DropdownMenuItem onClick={onDelete} className="text-destructive">
            {t('actions.delete', 'Delete')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
